import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError, type PagedData } from './common';
import { type RunnerType } from './daemon';

export type MemberRunnerModelRecord = {
  id: string;
  memberId: string;
  runnerType: RunnerType;
  label: string;
  value: string;
  isEnabled: boolean;
  sortOrder: number;
  maxInputTokens: number | null;
  fastModeSupported: boolean;
  supportedEffortLevels: string[];
  source: 'MANUAL' | 'DETECTED';
  isConfirmed: boolean;
  detectedAt: string | null;
  rejectedAt: string | null;
  machineScope?: { daemonIds: string[]; daemonLabels: string[] } | null;
  createdAt: string;
  updatedAt: string;
};

export type MemberRunnerModelQueryParams = {
  runnerType?: RunnerType;
  machineId?: string;
  search?: string;
  /** true=승인 대기만, false=승인 완료만. 생략하면 둘 다. */
  pending?: boolean;
  page?: number;
  pageSize?: number;
};

export type CreateMemberRunnerModelInput = {
  runnerType: RunnerType;
  label: string;
  value: string;
  sortOrder?: number;
  maxInputTokens?: number | null;
  fastModeSupported?: boolean;
  supportedEffortLevels?: string[];
};

export type UpdateMemberRunnerModelInput = {
  label?: string;
  value?: string;
  isEnabled?: boolean;
  sortOrder?: number;
  maxInputTokens?: number | null;
  fastModeSupported?: boolean;
  supportedEffortLevels?: string[];
  isConfirmed?: boolean;
  isRejected?: boolean;
};

export type MemberRunnerModelApprovalFilter = Omit<MemberRunnerModelQueryParams, 'page' | 'pageSize'>;

export type BulkUpdateMemberRunnerModelApprovalInput =
  | { action: 'approve' | 'reject'; ids: string[] }
  | { action: 'approve' | 'reject'; filter: MemberRunnerModelApprovalFilter };

type MemberRunnerModelEnvelope = {
  data: MemberRunnerModelRecord;
};

export const fetchMemberRunnerModels = async (
  token: string,
  params?: MemberRunnerModelQueryParams,
): Promise<PagedData<MemberRunnerModelRecord>> => {
  const query = new URLSearchParams();
  if (params?.runnerType) {
    query.set('runnerType', params.runnerType);
  }
  if (params?.machineId) {
    query.set('machineId', params.machineId);
  }
  if (params?.search) {
    query.set('search', params.search);
  }
  if (typeof params?.pending === 'boolean') {
    query.set('pending', String(params.pending));
  }
  if (typeof params?.page === 'number') {
    query.set('page', String(params.page));
  }
  if (typeof params?.pageSize === 'number') {
    query.set('pageSize', String(params.pageSize));
  }

  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/member-runner-models${query.size > 0 ? `?${query.toString()}` : ''}`,
    {
      cache: 'no-store',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch member runner models: ${response.status}`);
  }

  return (await response.json()) as PagedData<MemberRunnerModelRecord>;
};

export const createMemberRunnerModel = async (
  token: string,
  input: CreateMemberRunnerModelInput,
): Promise<MemberRunnerModelRecord> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/member-runner-models`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create member runner model: ${response.status}`);
  }

  const payload = (await response.json()) as MemberRunnerModelEnvelope;
  return payload.data;
};

export const updateMemberRunnerModel = async (
  token: string,
  id: string,
  input: UpdateMemberRunnerModelInput,
): Promise<MemberRunnerModelRecord> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/member-runner-models/${id}`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update member runner model: ${response.status}`);
  }

  const payload = (await response.json()) as MemberRunnerModelEnvelope;
  return payload.data;
};

export const bulkUpdateMemberRunnerModelApproval = async (
  token: string,
  input: BulkUpdateMemberRunnerModelApprovalInput,
): Promise<{ updated: number }> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/member-runner-models`, {
    method: 'PATCH',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to bulk update member runner models: ${response.status}`);
  }

  const payload = (await response.json()) as { data: { updated: number } };
  return payload.data;
};

export const deleteMemberRunnerModel = async (token: string, id: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/member-runner-models/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete member runner model: ${response.status}`);
  }
};
